import {
  ErrorResponse,
  PaginatedRequest,
  PaginatedResponse,
  ValidationErrorResponse,
} from '@opositandopn/http';
import { useToasterActions } from '@opositandopn/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useAxios } from '../../../context/AxiosContext';
import { FALLBACK_ERROR_CODE } from '../../../utils/api';

export type Status = 'draft' | 'published' | 'removed';

export type Visibility = 'public' | 'private';

export type DifficultyLevel = 'easy' | 'medium' | 'hard';

export interface AuditViewModel {
  username: string;
  action: string;
  description?: string;
  date: Date;
}

export interface ChoiceViewModel {
  id: string;
  text: string;
  isCorrect: boolean;
  metrics: ChoiceMetricsViewModel;
}

export interface TagViewModel {
  id: string;
  name: string;
}

export interface SchoolViewModel {
  id: string;
  name: string;
}

export interface SubjectViewModel {
  id: string;
  name: string;
}

export interface UnitViewModel {
  id: string;
  name: string;
}

export interface LessonViewModel {
  id: string;
  name: string;
}

export interface AssessmentViewModel {
  id: string;
  name: string;
}

export interface MetricsViewModel {
  totalAttempts: number;
  correctPercentage: number;
  incorrectPercentage: number;
  skippedPercentage: number;
}

export interface ChoiceMetricsViewModel {
  selectedCount: number;
  selectedPercentage: number;
}

export interface QuestionPreviewModel {
  id: string;
  text: string;
  status: Status;
  difficultyLevel: DifficultyLevel;
  visibility: Visibility;
  subject: SubjectViewModel;
  unit?: UnitViewModel;
  lesson?: LessonViewModel;
  assessments?: AssessmentViewModel[];
}

export interface MultipleChoiceQuestionViewModel {
  id: string;
  text: string;
  timeLimit: number;
  choices: ChoiceViewModel[];
  maintainsChoiceOrder: boolean;
  standalone: boolean;
  status: Status;
  difficultyLevel: DifficultyLevel;
  visibility: Visibility;
  tags: TagViewModel[];
  subject: SubjectViewModel;
  unit?: UnitViewModel;
  lesson?: LessonViewModel;
  explanation?: string;
  imageUrl?: string;
  bookmarked?: boolean;
  missesCount: number;
  auditLog: AuditViewModel[];
  metrics?: MetricsViewModel;
}

export interface CreateChoiceRequest {
  text: string;
  isCorrect?: boolean;
}

export interface CreateMultipleChoiceQuestionRequest {
  subjectId: string;
  text: string;
  timeLimit: number;
  choices: CreateChoiceRequest[];
  visibility: Visibility;
  difficultyLevel: DifficultyLevel;
  tagIds?: string[];
  maintainsChoiceOrder?: boolean;
  standalone?: boolean;
  unitId?: string;
  lessonId?: string;
  explanation?: string;
  base64Image?: string;
  status?: Status;
}

export interface CreateMultipleChoiceQuestionResponse {
  questionId: string;
}

export const useCreateMultipleChoiceQuestion = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(
    request: CreateMultipleChoiceQuestionRequest,
  ): Promise<CreateMultipleChoiceQuestionResponse> {
    const response = await instance.post<CreateMultipleChoiceQuestionResponse>(
      `/v2/subjects/${request.subjectId}/questions/mc`,
      request,
    );

    return response.data;
  }

  return useMutation<
    CreateMultipleChoiceQuestionResponse,
    AxiosError<ValidationErrorResponse>,
    CreateMultipleChoiceQuestionRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestions'],
      });
    },
    meta: {
      errorMessages: {
        invalid_operation: { title: 'Operación inválida' },
      },
    },
  });
};

export interface UpdateChoiceRequest {
  id: string;
  text: string;
  isCorrect?: boolean;
}

export interface UpdateMultipleChoiceQuestionRequest {
  id: string;
  subjectId: string;
  text?: string;
  timeLimit?: number;
  choices?: UpdateChoiceRequest[];
  visibility?: Visibility;
  difficultyLevel?: DifficultyLevel;
  tagIds?: string[];
  maintainsChoiceOrder?: boolean;
  standalone?: boolean;
  unitId?: string;
  lessonId?: string;
  explanation?: string;
  base64Image?: string;
  status?: Status;
}

export const useUpdateMultipleChoiceQuestion = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: UpdateMultipleChoiceQuestionRequest) {
    const response = await instance.patch(
      `/v2/subjects/${request.subjectId}/questions/mc/${request.id}`,
      request,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ValidationErrorResponse>,
    UpdateMultipleChoiceQuestionRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestion'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceMissedQuestions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceBookmarkedQuestions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessment'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessments'],
      });
    },
    meta: {
      errorMessages: {
        invalid_operation: { title: 'Operación inválida' },
      },
    },
  });
};

export type UpdateQuestionsStandaloneRequest = {
  subjectId: string;
  questionIds: string[];
  standalone: boolean;
};

export const useUpdateQuestionsStandalone = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: UpdateQuestionsStandaloneRequest) {
    const response = await instance.patch(
      `/v2/subjects/${request.subjectId}/questions/mc/standalone`,
      {
        questionIds: request.questionIds,
        standalone: request.standalone,
      },
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ValidationErrorResponse>,
    UpdateQuestionsStandaloneRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestion'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessment'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessments'],
      });
    },
    meta: {
      errorMessages: {
        invalid_operation: { title: 'Operación inválida' },
      },
    },
  });
};

export type GetMultipleChoiceQuestionsRequest = PaginatedRequest & {
  subjectId: string;
  params?: {
    text?: string;
    statuses?: Status[];
    visibilities?: Visibility[];
    difficultyLevels?: DifficultyLevel[];
    tagIds?: string[];
    userIds?: string[];
    unitId?: string;
    lessonId?: string;
  };
};

export const useGetMultipleChoiceQuestions = (
  request: GetMultipleChoiceQuestionsRequest,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<PaginatedResponse<QuestionPreviewModel>> {
    const response = await instance.get<
      PaginatedResponse<QuestionPreviewModel>
    >(`/v2/subjects/${request.subjectId}/questions/mc`, {
      params: {
        ...toQuestionListQueryParams(request),
        pageNumber: request.pageNumber,
        pageSize: request.pageSize,
      },
      paramsSerializer: {
        indexes: null,
      },
    });

    return response.data;
  }

  return useQuery({
    queryKey: ['getMultipleChoiceQuestions', request],
    queryFn,
  });
};

export type ExportMultipleChoiceQuestionsRequest = Omit<
  GetMultipleChoiceQuestionsRequest,
  'pageNumber' | 'pageSize'
>;

/**
 * One question of the export file. Dates stay ISO 8601 strings: the file is
 * saved as the API sent it.
 */
export interface MultipleChoiceQuestionExportModel {
  id: string;
  text: string;
  explanation?: string;
  timeLimit: number;
  maintainsChoiceOrder: boolean;
  standalone: boolean;
  status: Status;
  visibility: Visibility;
  difficultyLevel: DifficultyLevel;
  createdAt: string;
  updatedAt?: string;
  subject: SubjectViewModel;
  unit?: UnitViewModel;
  lesson?: LessonViewModel;
  tags: TagViewModel[];
  choices: ChoiceViewModel[];
  image?: {
    id: string;
    filename: string;
    url: string;
    urlExpiresAt: string;
  };
  /** Instructor assessments (simulacros) containing the question. */
  assessments: {
    id: string;
    name: string;
    description?: string;
    status: Status;
    visibility: Visibility;
    official: boolean;
    releaseDate: string;
    timeLimit: number;
    cutoffScore?: number;
    maintainsQuestionOrder: boolean;
    totalQuestions: number;
    /** 1-based position of the question within the assessment. */
    position: number;
  }[];
}

export interface ExportMultipleChoiceQuestionsResponse {
  /** The export file content, as the API sent it. */
  json: string;
  count: number;
}

export const useExportMultipleChoiceQuestions = () => {
  const { instance } = useAxios();

  // Read as text: up to 10,000 questions would otherwise go through the
  // response date hydration only to be serialized back for the file.
  async function mutationFn(
    request: ExportMultipleChoiceQuestionsRequest,
  ): Promise<ExportMultipleChoiceQuestionsResponse> {
    const response = await instance.get<string>(
      `/v2/subjects/${request.subjectId}/questions/mc/export`,
      {
        params: toQuestionListQueryParams(request),
        paramsSerializer: {
          indexes: null,
        },
        responseType: 'text',
      },
    );

    const questions = JSON.parse(
      response.data,
    ) as MultipleChoiceQuestionExportModel[];

    return { json: response.data, count: questions.length };
  }

  return useMutation<
    ExportMultipleChoiceQuestionsResponse,
    AxiosError<ErrorResponse>,
    ExportMultipleChoiceQuestionsRequest
  >({
    mutationFn,
    meta: {
      errorMessages: {
        invalid_operation: {
          title: 'Demasiadas preguntas',
          message:
            'La exportación admite hasta 10.000 preguntas. Acota los filtros, por ejemplo por fechas, y exporta por partes.',
        },
      },
    },
  });
};

function toQuestionListQueryParams(
  request: ExportMultipleChoiceQuestionsRequest,
) {
  return {
    fromTime: request.fromTime,
    toTime: request.toTime,
    sort: request.sort,
    text: request.params?.text,
    status: request.params?.statuses,
    visibility: request.params?.visibilities,
    difficultyLevel: request.params?.difficultyLevels,
    tagId: request.params?.tagIds,
    userId: request.params?.userIds,
    unitId: request.params?.unitId,
    lessonId: request.params?.lessonId,
  };
}

export type GetMultipleChoiceQuestionRequest = {
  subjectId: string;
  questionId: string;
};

export const useGetMultipleChoiceQuestion = (
  request: GetMultipleChoiceQuestionRequest,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<MultipleChoiceQuestionViewModel> {
    const response = await instance.get<MultipleChoiceQuestionViewModel>(
      `/v2/subjects/${request.subjectId}/questions/mc/${request.questionId}`,
    );

    return response.data;
  }

  return useQuery({
    queryKey: [
      'getMultipleChoiceQuestion',
      request.subjectId,
      request.questionId,
    ],
    queryFn,
  });
};

export type BookmarkMultipleChoiceQuestionRequest = {
  subjectId: string;
  questionId: string;
};

export const useBookmarkMultipleChoiceQuestion = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: BookmarkMultipleChoiceQuestionRequest) {
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/questions/mc/${request.questionId}/bookmark`,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    BookmarkMultipleChoiceQuestionRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestion'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceBookmarkedQuestions'],
      });
    },
  });
};

export const useUnbookmarkMultipleChoiceQuestion = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: BookmarkMultipleChoiceQuestionRequest) {
    const response = await instance.delete(
      `/v2/subjects/${request.subjectId}/questions/mc/${request.questionId}/bookmark`,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    BookmarkMultipleChoiceQuestionRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestion'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceBookmarkedQuestions'],
      });
    },
  });
};

export type CreateMultipleChoiceQuestionErrorReportRequest = {
  subjectId: string;
  questionId: string;
  message: string;
};

export const useCreateMultipleChoiceQuestionErrorReport = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();
  const { sendMessage } = useToasterActions();

  async function mutationFn(
    request: CreateMultipleChoiceQuestionErrorReportRequest,
  ) {
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/questions/mc/${request.questionId}/report`,
      {
        message: request.message,
      },
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    CreateMultipleChoiceQuestionErrorReportRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getErrorReports'],
      });

      sendMessage('¡Hecho!', 'Error reportado', 'success');
    },
  });
};

export type DeleteMultipleChoiceQuestionRequest = {
  subjectId: string;
  id: string;
};

export const useDeleteMultipleChoiceQuestion = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: DeleteMultipleChoiceQuestionRequest) {
    const response = await instance.delete(
      `/v2/subjects/${request.subjectId}/questions/mc/${request.id}`,
    );

    return response.data;
  }

  function forgetQuestion(request: DeleteMultipleChoiceQuestionRequest) {
    queryClient.invalidateQueries({
      queryKey: ['getMultipleChoiceQuestion', request.subjectId, request.id],
      refetchType: 'none',
    });
    queryClient.invalidateQueries({
      queryKey: ['getMultipleChoiceQuestions'],
    });
    queryClient.invalidateQueries({
      queryKey: ['getMultipleChoiceMissedQuestions'],
    });
    queryClient.invalidateQueries({
      queryKey: ['getMultipleChoiceBookmarkedQuestions'],
    });
    queryClient.invalidateQueries({
      queryKey: ['getErrorReports'],
    });
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    DeleteMultipleChoiceQuestionRequest
  >({
    mutationFn,
    onSuccess: (_, request) => {
      forgetQuestion(request);
    },
    onError: (e: AxiosError<ErrorResponse>, request) => {
      if (e.response?.data.code === 'invalid_operation') {
        forgetQuestion(request);
      }
    },
    meta: {
      errorMessages: {
        invalid_operation: {
          title: 'Operación inválida',
          message:
            'Esta pregunta ya no está disponible. Se ha actualizado el listado.',
          variant: 'warning',
        },
        [FALLBACK_ERROR_CODE]: {
          title: '¡Ups!',
          message:
            'Parece que ha ocurrido un error al eliminar la pregunta. Por favor, inténtalo de nuevo más tarde.',
        },
      },
    },
  });
};

export type RestoreMultipleChoiceQuestionRequest = {
  subjectId: string;
  id: string;
};

export const useRestoreMultipleChoiceQuestion = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: RestoreMultipleChoiceQuestionRequest) {
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/questions/mc/${request.id}/restore`,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    RestoreMultipleChoiceQuestionRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestion'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestions'],
      });
    },
  });
};

export type CreateRandomMultipleChoiceQuestionAttemptRequest = {
  subjectId: string;
  numberOfChoices?: number;
  unitIds?: string[];
  lessonIds?: string[];
  difficultyLevels?: DifficultyLevel[];
};

export type CreateMultipleChoiceQuestionAttemptCommandResult = {
  id: string;
  questionId: string;
  text: string;
  timeLimit: number;
  choices: {
    id: string;
    text: string;
  }[];
  imageUrl?: string;
  difficultyLevel: DifficultyLevel;
  subject: SubjectViewModel;
  unit?: UnitViewModel;
  lesson?: LessonViewModel;
  bookmarked?: boolean;
};

export const useCreateRandomMultipleChoiceQuestionAttempt = () => {
  const { instance } = useAxios();

  async function mutationFn(
    request: CreateRandomMultipleChoiceQuestionAttemptRequest,
  ) {
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/questions/mc/random/attempt`,
      {
        numberOfChoices: request.numberOfChoices,
        unitIds: request.unitIds,
        lessonIds: request.lessonIds,
        difficultyLevels: request.difficultyLevels,
      },
    );

    return response.data;
  }

  return useMutation<
    CreateMultipleChoiceQuestionAttemptCommandResult,
    AxiosError<ErrorResponse>,
    CreateRandomMultipleChoiceQuestionAttemptRequest
  >({
    mutationFn,
    meta: {
      errorMessages: {
        not_found: {
          title: '¡Ups!',
          message:
            'No se han encontrado preguntas para los temas seleccionados',
        },
      },
    },
  });
};

export type CreateMultipleChoiceQuestionAttemptRequest = {
  subjectId: string;
  questionId: string;
};

export const useCreateMultipleChoiceQuestionAttempt = () => {
  const { instance } = useAxios();

  async function mutationFn(
    request: CreateMultipleChoiceQuestionAttemptRequest,
  ) {
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/questions/mc/${request.questionId}/attempt`,
    );

    return response.data;
  }

  return useMutation<
    CreateMultipleChoiceQuestionAttemptCommandResult,
    AxiosError<ErrorResponse>,
    CreateMultipleChoiceQuestionAttemptRequest
  >({
    mutationFn,
  });
};

export type SubmitQuestionAttemptRequest = {
  subjectId: string;
  questionAttemptId: string;
  answer: string;
};

export type SubmitQuestionAttemptCommandResult<T> = {
  elapsedTime: number;
  questionAttempt: QuestionAttemptViewModel<T>;
};

export type QuestionAttemptViewModel<T> = {
  id: string;
  question: T;
  answer: string;
  correction: CorrectionViewModel;
};

export type CorrectionViewModel = {
  correctness: number;
  correctAnswer: string;
  feedback?: string;
};

export const useSubmitMultipleChoiceQuestionAttempt = () => {
  const { instance } = useAxios();

  async function mutationFn(request: SubmitQuestionAttemptRequest) {
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/questions/mc/attempts/${request.questionAttemptId}/submit`,
      {
        answer: request.answer,
      },
    );

    return response.data;
  }

  return useMutation<
    SubmitQuestionAttemptCommandResult<MultipleChoiceQuestionViewModel>,
    AxiosError<ErrorResponse>,
    SubmitQuestionAttemptRequest
  >({
    mutationFn,
  });
};

export type GetMissedQuestionsRequest = {
  subjectId: string;
};

export type MissViewModel = {
  question: QuestionPreviewModel;
  createdAt: Date;
  providedAnswer: string;
  feedback?: string;
};

export const useGetMultipleChoiceMissedQuestions = (
  request: GetMissedQuestionsRequest,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<MissViewModel[]> {
    const response = await instance.get<MissViewModel[]>(
      `/v2/subjects/${request.subjectId}/questions/mc/misses`,
    );

    return response.data;
  }

  return useQuery({
    queryKey: ['getMultipleChoiceMissedQuestions', request.subjectId],
    queryFn,
  });
};

export type RemoveMissedQuestionsRequest = {
  subjectId: string;
};

export const useRemoveMultipleChoiceMissedQuestions = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: RemoveMissedQuestionsRequest) {
    const response = await instance.delete(
      `/v2/subjects/${request.subjectId}/questions/mc/misses`,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    RemoveMissedQuestionsRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceMissedQuestions'],
      });
    },
  });
};

export type GetBookmarkedQuestionsRequest = {
  subjectId: string;
};

export type BookmarkViewModel = {
  question: QuestionPreviewModel;
  createdAt: Date;
};

export const useGetMultipleChoiceBookmarkedQuestions = (
  request: GetBookmarkedQuestionsRequest,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<BookmarkViewModel[]> {
    const response = await instance.get<BookmarkViewModel[]>(
      `/v2/subjects/${request.subjectId}/questions/mc/bookmarks`,
    );

    return response.data;
  }

  return useQuery({
    queryKey: ['getMultipleChoiceBookmarkedQuestions', request.subjectId],
    queryFn,
  });
};

export type RemoveBookmarkedQuestionsRequest = {
  subjectId: string;
};

export const useRemoveMultipleChoiceBookmarkedQuestions = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: RemoveBookmarkedQuestionsRequest) {
    const response = await instance.delete(
      `/v2/subjects/${request.subjectId}/questions/mc/bookmarks`,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    RemoveBookmarkedQuestionsRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceBookmarkedQuestions'],
      });
    },
  });
};

export type ExplainQuestionAttemptRequest = {
  subjectId: string;
  questionAttemptId: string;
};

export type ExplainQuestionAttemptCommandResult = {
  explanation: string;
};

const EXPLAIN_QUESTION_ATTEMPT_GC_TIME = 30 * 60 * 1000;

export const useExplainMultipleChoiceQuestionAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  const explain = (
    request: ExplainQuestionAttemptRequest,
  ): Promise<ExplainQuestionAttemptCommandResult> =>
    queryClient.fetchQuery({
      queryKey: [
        'explainMultipleChoiceQuestionAttempt',
        request.subjectId,
        request.questionAttemptId,
      ],
      queryFn: async () => {
        const response =
          await instance.get<ExplainQuestionAttemptCommandResult>(
            `/v2/subjects/${request.subjectId}/questions/mc/attempts/${request.questionAttemptId}/explain`,
          );

        return response.data;
      },
      staleTime: Infinity,
      gcTime: EXPLAIN_QUESTION_ATTEMPT_GC_TIME,
    });

  return { explain };
};

export type ExplainQuestionRequest = {
  subjectId: string;
  questionId: string;
  answerId?: string;
};

export type ExplainQuestionCommandResult = {
  explanation: string;
};

const EXPLAIN_QUESTION_GC_TIME = 30 * 60 * 1000;

export const useExplainMultipleChoiceQuestion = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  const explain = (
    request: ExplainQuestionRequest,
  ): Promise<ExplainQuestionCommandResult> =>
    queryClient.fetchQuery({
      queryKey: [
        'explainMultipleChoiceQuestion',
        request.subjectId,
        request.questionId,
        request.answerId ?? '',
      ],
      queryFn: async () => {
        const response = await instance.get<ExplainQuestionCommandResult>(
          `/v2/subjects/${request.subjectId}/questions/mc/${request.questionId}/explain`,
          {
            params: {
              answerId: request.answerId,
            },
          },
        );

        return response.data;
      },
      staleTime: Infinity,
      gcTime: EXPLAIN_QUESTION_GC_TIME,
    });

  return { explain };
};
